import React from 'react';
import { MessageCircle, Share2, Link as LinkIcon } from 'lucide-react';
import Button from '../common/Button';
import { useToast } from '../../context/ToastContext';
import { copyToClipboard } from './obituaryUtils';

/**
 * ShareButtons - 부고 공유 버튼 그룹
 * 카카오톡, 문자, 링크복사 공유 기능 제공
 */
const ShareButtons = ({
  obituary,
  className = '',
  variant = 'row' // 'row' | 'column'
}) => {
  const { showToast } = useToast();

  // 상세 페이지 링크 (App.js 라우트와 통일: /obituaries/:id)
  const detailUrl = `${window.location.origin}/obituaries/${obituary.id}`;

  // 공유 메시지 (상세 링크 포함)
  const buildMessage = () =>
    [
      '[부고 알림]',
      `고인: 故 ${obituary.deceasedName}`,
      `장례식장: ${obituary.funeralHome}`,
      `빈소: ${obituary.funeralRoom}`,
      `발인: ${obituary.departureDate} ${obituary.departureTime}`,
      '',
      `부고 보기: ${detailUrl}`,
    ].join('\n');

  // 카카오톡 공유 (SDK 없으면 Web Share API → 링크복사 폴백)
  const handleKakaoShare = async () => {
    const message = buildMessage();

    if (navigator.share) {
      try {
        await navigator.share({
          title: `故 ${obituary.deceasedName} 부고`,
          text: message,
          url: detailUrl,
        });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return; // 사용자가 취소
        // 그 외에는 폴백으로 진행
      }
    }

    const ok = await copyToClipboard(message);
    showToast(
      ok
        ? '공유 내용이 복사되었습니다. 카카오톡에 붙여넣어 전달하세요.'
        : '카카오톡 공유는 실제 환경에서 사용 가능합니다.',
      ok ? 'success' : 'info'
    );
  };

  // 문자 공유 (sms: 스킴, 미지원 시 Web Share / 복사 폴백)
  const handleSmsShare = async () => {
    const message = buildMessage();

    if (navigator.share) {
      try {
        await navigator.share({
          title: `故 ${obituary.deceasedName} 부고`,
          text: message,
          url: detailUrl,
        });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }

    // sms: 스킴 시도 (모바일에서 문자 앱 실행)
    try {
      const smsUrl = `sms:?&body=${encodeURIComponent(message)}`;
      window.location.href = smsUrl;
      showToast('문자 메시지 앱을 실행합니다.', 'info');
    } catch (err) {
      const ok = await copyToClipboard(message);
      showToast(
        ok ? '공유 내용이 복사되었습니다.' : '문자 공유에 실패했습니다.',
        ok ? 'success' : 'error'
      );
    }
  };

  // 링크 복사
  const handleCopyLink = async () => {
    const ok = await copyToClipboard(detailUrl);
    showToast(
      ok ? '링크가 복사되었습니다.' : '링크 복사에 실패했습니다.',
      ok ? 'success' : 'error'
    );
  };

  const containerClass = variant === 'row'
    ? 'flex gap-2'
    : 'flex flex-col gap-2';

  return (
    <div className={`${containerClass} ${className}`}>
      <Button
        variant="outline"
        size="sm"
        onClick={handleKakaoShare}
        className="flex items-center justify-center gap-2"
      >
        <MessageCircle size={18} />
        <span>카카오톡</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={handleSmsShare}
        className="flex items-center justify-center gap-2"
      >
        <Share2 size={18} />
        <span>문자</span>
      </Button>

      <Button
        variant="outline"
        size="sm"
        onClick={handleCopyLink}
        className="flex items-center justify-center gap-2"
      >
        <LinkIcon size={18} />
        <span>링크복사</span>
      </Button>
    </div>
  );
};

export default ShareButtons;
